<template>
  <div id="confirmation">
    <div class="main">
      <!-- Header -->
      <div
        v-if="
          bookingResponse.bookingInformation.selectedPaymentMethod
            .paymentOptionName == 'CASH' ||
            bookingResponse.bookingInformation.selectedPaymentMethod
              .paymentOptionName == 'EFT'
        "
      >
        <h1 class="confirmation-header is-helvetica">
          Thank you for booking your flight with us.
        </h1>
        <p>
          <b style="color: red">This confirmation is not valid for travel!</b>
        </p>
        <p class="width-100" v-if="pnpReferenceNumber">
          <b>Pnp reference number: </b> {{ pnpReferenceNumber }}
        </p>
        <p>
          <b>Payment due by: </b>
          {{ formatDate(bookingResponse.bookingInformation.lastPayDate) }}
        </p>
        <p>
          Once we have processed your payment we will send you your e-ticket in
          a separate email. For flights departing in more than one day this may
          take up to 24 hours. Please take a minute to verify all your details,
          especially the passenger names and travel dates. Once your ticket has
          been issued airline penalties may apply to make any changes or process
          any cancellations.
        </p>
      </div>
      <div v-else>
        <h1 class="confirmation-header is-helvetica" data-cy="confirmation-success">
          Your booking was successful!
        </h1>
        <p>
          <b>Your Investec Travel booking reference:</b>
          <br />
          {{ bookingResponse.bookingInformation.bookingReferenceNo }}
        </p>
      </div>
      <!-- End of Header -->
      <hr class="content-break" />

      <!-- start of upsell -->
      <div class="upsell">
        <div class="upsell-card">
          <img
            src="@/assets/images/man-in-car-window.png"
            alt=""
            class="upsell-image"
          />
          <h2>Book a car</h2>
          <p>
            We use multiple agencies so you are guaranteed the best possible
            rate.
          </p>

          <a class="button-dark" href="https://cars.investectravel.com/"
            >Browse cars</a
          >
        </div>

        <div class="upsell-card card-border">
          <img src="@/assets/images/legs-off-bed.png" class="upsell-image" />
          <h2>Book a hotel</h2>
          <p>
            Browse thousands of hotels to see what suits you and your budget.
          </p>
          <a class="button-dark" href="https://hotels.secure.investec.com/"
            >Browse hotels</a
          >
        </div>

        <div class="upsell-card">
          <img
            src="@/assets/images/family-at-airport.jpeg"
            class="upsell-image"
          />
          <h2>Buy travel insurance</h2>
          <p>
            Travel with peace of mind and ensure you are adequately covered for
            specified unforeseen events.
          </p>

          <a
            class="button-dark"
            href="https://www.investectravel.com/insurance/"
            >Visit Bryte</a
          >
        </div>
      </div>
      <!-- End of upsell -->
      <hr class="content-break" />

      <!-- Content -->
      <div class="summary-list">
        <div>
          <h3 class="is-helvetica" style="font-weight: bold">Payer</h3>
          <b class="no-margin">Name:</b>
          {{ payer.title }} {{ payer.firstName }} {{ payer.lastName }}
          <br />
          <b class="no-margin">Email:</b>
          {{ payer.email }}
          <br />
          <b class="no-margin">Contact no:</b>
          {{ payer.mobileNumber }}
        </div>

        <div>
          <h3 class="is-helvetica" style="font-weight: bold">
            Price breakdown
          </h3>
          Adult airfare:
          <b>R{{ Math.ceil(priceResponse.fareBreakdown.adults.baseFare) }}</b>
          <span v-if="priceResponse.fareBreakdown.youngAdults">
            <br />Young adult airfare:
            <b
              >R{{
                Math.ceil(priceResponse.fareBreakdown.youngAdults.baseFare)
              }}</b
            >
          </span>
          <span v-if="priceResponse.fareBreakdown.children">
            <br />Child airfare:
            <b
              >R{{
                Math.ceil(priceResponse.fareBreakdown.children.baseFare)
              }}</b
            >
          </span>
          <span v-if="priceResponse.fareBreakdown.infants">
            <br />Infant airfare:
            <b
              >R{{ Math.ceil(priceResponse.fareBreakdown.infants.baseFare) }}</b
            >
          </span>
          <span v-if="paymentCharge > 0">
            <br />Payment option fee:
            <b>R{{ Math.ceil(paymentCharge) }}</b>
          </span>
          <span v-if="baggageCost > 0">
            <br />Total baggage cost:
            <b>R{{ Math.ceil(baggageCost) }}</b>
          </span>
          <br />Total airport taxes:
          <b>R{{ Math.ceil(priceResponse.fareBreakdown.taxAmount) }}</b>
          <span v-if="productsCost > 0">
            <br />Total extras:
            <b>R{{ Math.ceil(productsCost) }}</b>
          </span>
          <br />
          Total price:
          <b
            >R{{
              Math.ceil(
                priceResponse.totalAmount +
                  productsCost +
                  baggageCost +
                  paymentCharge
              )
            }}</b
          >
        </div>

        <div>
          <h3 class="is-helvetica" style="font-weight: bold">Passenger(s)</h3>
          <div
            v-for="(passenger, index) in allPassengers"
            :key="`passenger-${index}`"
          >
            <b class="no-margin">Name:</b>
            {{ passenger.title }} {{ passenger.firstName }}
            {{ passenger.middleName }} {{ passenger.lastName }}
            <br />
            <b class="no-margin">Dob:</b>
            {{ passenger.dob.day }}/{{ passenger.dob.month }}/{{
              passenger.dob.year
            }}
            <span v-if="passenger.documentNumber">
              <br />
              <b class="no-margin">Passport/I.D. number:</b>
              {{ passenger.documentNumber }}
            </span>
            <br /><br />
          </div>
        </div>
      </div>

      <div>
        (Includes online booking fee.)
      </div>

      <hr class="content-break" />
      <h1 class="price is-helvetica">Itinerary breakdown</h1>

      <div class="itinerary-breakdown">
        <div class="trip-split">
          <ConfirmationFlight
            :flight-details="selectedFlights.outbound"
          ></ConfirmationFlight>
          <div class="mobile-width-100" v-if="flags.isDomesticReturn">
            <ConfirmationFlight
              :flight-details="selectedFlights.return"
            ></ConfirmationFlight>
          </div>
        </div>
        <div
          type="is-primary"
          class="review-content"
          v-for="flight in allFlights"
          :key="flight.index"
        >
          <b>Flight:</b>
          {{ searchResults.airlineNames[flight.airlineCode] }}
          {{ flight.flightNumber }}
          <span class="dulled-text" v-if="flight.opAirlineCode">
            - operated by
            {{
              searchResults.airlineNames[flight.opAirlineCode] ||
                flight.opAirlineCode.toLowerCase()
            }}
          </span>
          <br />
          <b>Depart:</b>
          {{ formatDate(flight.departureDateTime) }} from ({{
            flight.origCode
          }})
          {{ determineLocationFromCode(flight.origCode).airport }}
          <br />
          <b>Arrive:</b>
          {{ formatDate(flight.arrivalDateTime) }} at ({{ flight.destCode }})
          {{ determineLocationFromCode(flight.destCode).airport }}
          <br />
          <b>Cabin:</b>
          {{
            flight.cabinClass.charAt(0).toUpperCase() +
              flight.cabinClass.toLowerCase().slice(1)
          }}
        </div>
      </div>
      <hr class="content-break" />
      <h1 class="price is-helvetica">Baggage allowance</h1>
      <BaggageDisplay :allPassengers="allPassengers" />
      <div style="width: 100% !important; ">
        <b-button class="navigation-button" @click="openLink">
          Back to search
        </b-button>
      </div>
      <!-- End of Content -->
    </div>
  </div>
</template>

<script>
import ConfirmationFlight from "@/components/ConfirmationFlight.vue";
import { mapState, mapGetters } from "vuex";
import moment from "moment";
import Api from "@/services/Api.service.js";
import BaggageDisplay from "@/components/BaggageDisplay.vue";

export default {
  name: "confirmation",
  computed: {
    ...mapState([
      "selectedFlights",
      "searchResults",
      "priceResponse",
      "bookingResponse",
      "flags",
      "payer",
      "passengers",
      "urlSearchParameters",
      "searchParameters",
      "isInternational",
      "extraDetails",
      "baggageCost",
      "products",
      "paymentCharge",
      "pnpReferenceNumber",
      "specialFareWarningSeen",
      "ukTransitWarningSeen",
      "noBaggageWarningSeen",
    ]),
    ...mapGetters(["hasEmiratesHClassFlights"]),

    allPassengers() {
      return this.passengers.adults
        .concat(this.passengers.youngAdults)
        .concat(this.passengers.children)
        .concat(this.passengers.infants);
    },
    allFlights() {
      let flights = [];
      for (let itinerary of this.priceResponse.itineraries) {
        for (let leg of itinerary.odoList) {
          for (let segment of leg.segments) {
            segment.specialAttributes = itinerary.specialServiceAttributes;
            flights.push(segment);
          }
        }
      }
      return flights;
    },
    productsCost() {
      let result = 0;
      this.products.forEach((selected) => {
        if (selected.selectedValue) {
          result += this.priceResponse.products.find((e) => e.id == selected.id)
            .amount;
        }
      });
      return result;
    },
  },
  methods: {
    isEmpty(obj) {
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) return false;
      }
      return true;
    },
    formatDate(date) {
      return moment(date).format("D MMMM YYYY [at] HH:mm");
    },
    determineLocationFromCode(code) {
      if (this.searchResults) {
        return this.searchResults.airportInfos.find((e) => {
          return e.iataCode == code;
        });
      }
      return undefined;
    },
    sendConfirmationMail() {
      Api.sendConfirmationMail({
        email: this.payer.email,
        booking: this.bookingResponse,
        selectedFlights: this.selectedFlights,
        passengers: this.passengers.adults
          .concat(this.passengers.youngAdults)
          .concat(this.passengers.children)
          .concat(this.passengers.infants),
        priceResponse: this.priceResponse,
        flights: this.allFlights,
        payer: this.payer,
        pnpReferenceNumber: this.pnpReferenceNumber
          ? this.pnpReferenceNumber
          : null,
        type: this.paymentType,
        airlineNames: this.searchResults.airlineNames,
        airportInfos: this.searchResults.airportInfos,
      });
    },
    openLink() {
      if (typeof window !== "undefined") {
        window.open(
          "https://www.investectravel.com/flights-cars-hotels/",
          "_self"
        );
      } else {
        console.error("Window object is not available");
      }
    },
    async logSpecialFareBookingConfirmed() {
      try {
        await Api.logSpecialFareWarning({
          sessionID: this.$store.state.session,
          bookingReference: this.bookingResponse.bookingInformation
            .bookingReferenceNo,
          action: "h_class_warning_displayed",
          timestamp: new Date().toISOString(),
        });
        this.$store.commit("setSpecialFareWarningSeen", false);
      } catch (error) {
        console.warn("Failed to log special fare booking confirmation:", error);
      }
    },
    async logUKTransitBookingConfirmed() {
      try {
        await Api.logSpecialFareWarning({
          sessionID: this.$store.state.session,
          bookingReference: this.bookingResponse.bookingInformation
            .bookingReferenceNo,
          action: "uk_transit_warning_displayed",
          timestamp: new Date().toISOString(),
        });
        this.$store.commit("setUKTransitWarningSeen", false);
      } catch (error) {
        console.warn("Failed to log UK transit booking confirmation:", error);
      }
    },
    async logNoBaggageBookingConfirmed() {
      try {
        await Api.logSpecialFareWarning({
          sessionID: this.$store.state.session,
          bookingReference: this.bookingResponse.bookingInformation
            .bookingReferenceNo,
          action: "no_baggage_warning_displayed",
          timestamp: new Date().toISOString(),
        });
        this.$store.commit("setNoBaggageWarningSeen", false);
      } catch (error) {
        console.warn("Failed to log no baggage booking confirmation:", error);
      }
    },
  },
  mounted() {
    this.sendConfirmationMail();

    if (this.specialFareWarningSeen) {
      this.logSpecialFareBookingConfirmed();
    }

    if (this.ukTransitWarningSeen) {
      this.logUKTransitBookingConfirmed();
    }

    if (this.noBaggageWarningSeen) {
      this.logNoBaggageBookingConfirmed();
    }
  },
  data: function() {
    return {
      isTrue: true,
    };
  },
  components: {
    ConfirmationFlight,
    BaggageDisplay,
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/settings.scss";

#confirmation {
  margin: 2rem auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.main {
  align-self: flex-start;
  margin: 0.75rem;
  padding: 40px;
  max-width: 1220px;
  padding: 22px;
  width: 100%;
  background: #ffffff;
  border-top: 4px solid $brandOranges;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  display: flex;
  gap: 16px;
  flex-direction: column;
  z-index: 10;
}

.confirmation-header {
  margin-bottom: 16px;
  h1 {
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 400;
    font-size: 40px;
    line-height: 48px;
    color: #30384a;
  }
}

.content-break {
  margin: 16px 0 !important;
  background-color: $brandBlue !important;
  opacity: 0.1 !important;
  height: 1px;
}

.navigation-button,
.navigation-button:hover {
  border: 2px solid #30384a !important;
  background-color: #30384a !important;
  color: #fff;
  border-radius: 104px;
}

.passenger-count {
  margin-right: 5px;
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 6px;
  padding-right: 6px;
  color: black;
  border-radius: 4px;
  background-color: rgba(170, 166, 166, 0.881);
  display: flex;
  justify-content: center;
  align-items: center;
}

.is-helvetica {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.review-content {
  margin-bottom: 1rem;
}

.trip-split {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.no-margin {
  margin: 0;
}

.message-body {
  padding: 1em !important;
}

.bold {
  font-weight: bold;
}

.itinerary-breakdown {
  max-width: 720px;
  margin-bottom: 0.67em;
}

.mobile-width-100 {
  width: 100%;
}

.width-100 {
  width: 100%;
}

.content-sized {
  max-width: 720px;
  margin: 0.75em auto !important;
}

@media (min-width: 768px) {
  .summary-list {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    div {
      grid-column: span 1 / span 1;
    }
  }
}

.summary-list {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  text-align: left;
  max-width: 720px;
}

.details-divider {
  width: 100%;
  border-bottom: 2px dashed $grey;
}

h1 {
  font-weight: bold;
  font-size: 1.1em;
}

@media (min-width: 1180px) {
  h1 {
    font-size: 1.4em;
  }

  .mobile-width-100 {
    width: 100%;
  }

  .upsell {
    flex-wrap: nowrap;
  }

  .card-border {
    border-left: 2px solid #ecece9;
    border-right: 2px solid #ecece9;
  }

  #confirmation {
    margin: 4rem auto 2rem auto;
  }
}

@media (max-width: 781px) {
  .upsell {
    gap: 32px;

    .upsell-card {
      h2 {
        padding-top: 0px;
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/settings.scss";
.message.is-primary .message-body {
  border-color: $primary-color !important;
  color: #04333d;
}

//upsell section styling
.upsell {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.upsell-card {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  padding: 0 40px;
  text-align: center;

  .button-dark {
    margin-top: auto;
  }

  h2 {
    font-family: "Libre Caslon Text", serif;
    font-size: 200%;
    padding-top: 20px;
    padding-bottom: 15px;
  }
  p {
    font-family: "Inter", serif;
    padding-bottom: 20px;
  }
}

.button-dark {
  color: white !important;
  font-family: "Inter", sans-serif;
  white-space: nowrap;
  box-sizing: border-box;

  /* Auto layout */

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;

  width: 182px;
  height: 40px;

  background: #30384a;
  border: 2px solid #30384a;
  border-radius: 20px;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.upsell-image {
  height: 170px;
  width: 100%;
  max-width: 220px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 20px;
}

.key {
  text-align: left;
  opacity: 0.7;
}
</style>
